// SettingsModal.jsx

import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

// Context
import { useBase } from 'context/BaseContext';
import { useBaseRoster } from 'context/BaseRosterContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Device Detection
import { useIsCompactSplit, useIsMobile } from 'hooks/useDeviceDetect';

// Services
import { deleteBaseByKey } from 'services/bases';
import { getBaseKey } from 'services/bases/BaseReadService';
import { createSetting, updateSetting } from 'services/settings/SettingWriteService';

// Utilities
import { buildActiveBaseSettingPatch } from 'utilities/baseSettingsKeys';

// Hooks
import useBaseAccess from 'hooks/useBaseAccess';
import useBaseSeatLimit from 'hooks/useBaseSeatLimit';
import useBaseSwitcher from 'hooks/useBaseSwitcher';
import useSettingsMembers from './hooks/useSettingsMembers';

// CSS
import './SettingsModal.css';

// Icons
import Icon from 'svg/Icon';

// Components
import SettingsModalButton from './button/SettingsModalButton';
import SettingsAboutPane from './components/SettingsAboutPane';
import SettingsAiPane from './components/SettingsAiPane';
import SettingsCollectionsPane from './components/SettingsCollectionsPane';
import SettingsIntegrationsPane from './components/SettingsIntegrationsPane';
import SettingsMembersPane from './components/SettingsMembersPane';
import SettingsPlanPane from './components/SettingsPlanPane';
import SettingsDocumentsPane from './components/SettingsDocumentsPane';
import SettingsQuickBooksPane from './components/SettingsQuickBooksPane';
import SettingsPurchaseOrdersPane from './components/SettingsPurchaseOrdersPane';
import SettingsRolesPane from './components/SettingsRolesPane';
import SettingsSequencesPane from './components/SettingsSequencesPane';
import SettingsThemePane from './components/SettingsThemePane';
import Confirm from 'components/confirm/Confirm';
import Modal from 'components/modal/Modal';

const TAB_ABOUT = 'about';
const TAB_MEMBERS = 'members';
const TAB_ROLES = 'roles';
const TAB_COLLECTIONS = 'collections';
const TAB_SOURCES = 'sources';
const TAB_DOCUMENTS = 'documents';
const TAB_FINANCIALS = 'financials';
const TAB_PURCHASE_ORDERS = 'purchase-orders';
const TAB_SEQUENCES = 'sequences';
const TAB_INTEGRATIONS = 'integrations';
const TAB_AI = 'ai';
const TAB_PLAN = 'plan';
const TAB_THEME = 'theme';

const TAB_LABELS = {
    [TAB_ABOUT]: 'About',
    [TAB_MEMBERS]: 'Members',
    [TAB_ROLES]: 'Roles',
    [TAB_COLLECTIONS]: 'Collections',
    [TAB_SOURCES]: 'Sources',
    [TAB_DOCUMENTS]: 'Documents',
    [TAB_FINANCIALS]: 'Financials',
    [TAB_PURCHASE_ORDERS]: 'Purchase Orders',
    [TAB_SEQUENCES]: 'Sequences',
    [TAB_INTEGRATIONS]: 'Integrations',
    [TAB_AI]: 'AI',
    [TAB_PLAN]: 'Plan',
    [TAB_THEME]: 'Theme',
};

const ROLE_OWNER = 'owner';
const ROLE_ADMIN = 'admin';

// Legacy identity tabs that now live under About.
const ABOUT_ALIAS_TABS = new Set([
    'name',
    'icon',
    'cover',
]);

const REMOVED_SETTINGS_TABS = new Set([
    'profiles',
    'theme-default',
]);

function resolveInitialSettingsTab(requestedTab, { canSeeBaseSettings, canSeeSources } = {}) {
    const normalizedTab = String(requestedTab || '').trim();

    if (normalizedTab === 'embed' || normalizedTab === 'path') {
        return canSeeBaseSettings ? TAB_MEMBERS : (canSeeSources ? TAB_SOURCES : TAB_MEMBERS);
    }

    if (ABOUT_ALIAS_TABS.has(normalizedTab)) {
        return canSeeBaseSettings ? TAB_ABOUT : (canSeeSources ? TAB_SOURCES : TAB_MEMBERS);
    }

    if (REMOVED_SETTINGS_TABS.has(normalizedTab)) {
        return canSeeBaseSettings ? TAB_MEMBERS : (canSeeSources ? TAB_SOURCES : TAB_MEMBERS);
    }

    if (normalizedTab && TAB_LABELS[normalizedTab]) {
        if (normalizedTab === TAB_SOURCES && canSeeSources) return TAB_SOURCES;
        if (normalizedTab !== TAB_SOURCES && canSeeBaseSettings) return normalizedTab;
    }

    if (canSeeBaseSettings) return TAB_MEMBERS;
    if (canSeeSources) return TAB_SOURCES;
    return TAB_MEMBERS;
}

/**
 * SettingsModal
 *
 * Renders space settings panels for the switcher popup.
 *
 * @param {boolean} isOpen - Whether the modal is currently open.
 * @param {function} setOpen - Setter for modal visibility.
 * @param {string|null} initialTab - Optional tab key to activate when the modal opens.
 * @param {object} base - Active base model.
 * @param {function} onInviteClick - Callback for opening member invite flow.
 * @param {function} onClose - Callback fired after modal close.
 *
 * @returns {ReactNode} The rendered settings modal.
 */
const SettingsModal = React.memo(({
    isOpen,
    setOpen,
    initialTab = null,
    preferredPlanKey = '',
    base,
    onInviteClick,
    onClose,
}) => {

    // ----- CONTEXT ----- //

    // Base Context / Theme Context

    const {
        theme,
    } = useTheme();

    // User Context
    const { user, userSettings, setUserSettings } = useUser();
    const { setActiveBase } = useBase();
    const { bases: rosterBases, hideBase, unhideBase } = useBaseRoster();
    const { handleBaseSelect } = useBaseSwitcher();

    // ----- DEVICE DETECTION ----- //

    // Device breakpoint detection.

    const isMobile = useIsMobile();
    // Side nav is too wide for iPad dialogs; use the dropdown chrome instead.
    const isCompactChrome = useIsCompactSplit();

    // ----- HOOKS ----- //

    // Base permission helpers.

    const { baseRole, baseTier, isRoleLoading, can } = useBaseAccess(base);

    // ----- STATE ----- //

    // Active Tab State

    const [activeTab, setActiveTab] = useState(TAB_MEMBERS);

    // Delete-base confirm modal state.
    const [deleteBaseConfirmOpen, setDeleteBaseConfirmOpen] = useState(false);

    // Delete-base in-flight state.
    const [isDeletingBase, setIsDeletingBase] = useState(false);

    // Mobile dropdown menu open state.
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Members role-change permission.
    const canChangeRoles = can('members.changeRole');

    // Members removal permission.
    const canDeleteMembers = can('members.remove');

    // Invite creation permission.
    const canInviteMembersBase = can('invite.create');

    // Pending invite visibility for seat-limit calculations.
    const canListPendingInvites = can('invite.list');

    // Base edit permission.
    const canEditBase = can('base.update');

    // Base delete permission.
    const canDeleteBase = can('base.delete');

    // Owner/admin-only space settings gate.
    const canSeeBaseSettings = baseTier === ROLE_OWNER || baseTier === ROLE_ADMIN;

    const canSeeSources = false;

    // Active tab display label.
    const activeTabLabel = TAB_LABELS[activeTab] || 'Settings';

    // ----- HOOKS ----- //

    // Members pane state and actions.

    const {
        sortedMembers,
        members,
        loading: membersLoading,
        subtitle: membersSubtitle,
        errorMessage,
        statusMessage,
        clearMessages,
        canManageTarget,
        handleRoleChange,
        handleRemoveMember,
        roleOptions,
        getMemberRoleLabel,
    } = useSettingsMembers({
        isOpen: isOpen && canSeeBaseSettings,
        baseKey: base?.key,
        base,
        baseRole,
        baseTier,
        userKey: user?.key,
        canChangeRoles,
        canDeleteMembers,
    });

    const seatLimit = useBaseSeatLimit({
        canListPendingInvites,
        isActive: isOpen && canSeeBaseSettings && activeTab === TAB_MEMBERS,
        memberCount: members.length,
        planKey: base?.planKey,
        baseKey: base?.key,
        baseMemberCount: base?.memberCount,
    });

    const isAtSeatLimit = seatLimit.isAtSeatLimit;
    const canInviteMembers = canInviteMembersBase && !isAtSeatLimit;
    const membersPaneSubtitle = membersLoading || seatLimit.loading
        ? 'Loading members...'
        : seatLimit.membersSubtitle || membersSubtitle;

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!isOpen || isRoleLoading) return;

        setActiveTab(resolveInitialSettingsTab(initialTab, {
            canSeeBaseSettings,
            canSeeSources,
        }));
    }, [canSeeBaseSettings, canSeeSources, initialTab, isOpen, isRoleLoading]);

    useEffect(() => {
        if (isCompactChrome && isOpen) return;

        setIsMobileMenuOpen(false);
    }, [isCompactChrome, isOpen]);

    // ----- HANDLERS ----- //

    /**
     * Closes settings and clears member pane status state.
     */

    const handleClose = () => {
        clearMessages();
        setDeleteBaseConfirmOpen(false);
        setIsMobileMenuOpen(false);
        setOpen && setOpen(false);
        onClose && onClose();
    };

    /**
     * Sets the active settings tab and dismisses the mobile menu.
     *
     * @param {string} nextTab - The tab key to activate.
     */
    const handleTabChange = (nextTab) => {
        clearMessages();
        setActiveTab(nextTab);
        setIsMobileMenuOpen(false);
    };

    /**
     * Activates the about tab.
     */
    const handleAboutTabClick = () => {
        handleTabChange(TAB_ABOUT);
    };

    /**
     * Activates the members tab.
     */
    const handleMembersTabClick = () => {
        handleTabChange(TAB_MEMBERS);
    };

    /**
     * Activates the roles tab.
     */
    const handleRolesTabClick = () => {
        handleTabChange(TAB_ROLES);
    };

    /**
     * Activates the collections tab.
     */
    const handleCollectionsTabClick = () => {
        handleTabChange(TAB_COLLECTIONS);
    };

    /**
     * Activates the sources tab.
     */
    const handleSourcesTabClick = () => {
        handleTabChange(TAB_SOURCES);
    };

    /**
     * Activates the documents tab.
     */
    const handleDocumentsTabClick = () => {
        handleTabChange(TAB_DOCUMENTS);
    };

    /**
     * Activates the financials / QuickBooks tab.
     */
    const handleFinancialsTabClick = () => {
        handleTabChange(TAB_FINANCIALS);
    };

    /**
     * Activates the purchase orders tab.
     */
    const handlePurchaseOrdersTabClick = () => {
        handleTabChange(TAB_PURCHASE_ORDERS);
    };

    /**
     * Activates the sequences tab.
     */
    const handleSequencesTabClick = () => {
        handleTabChange(TAB_SEQUENCES);
    };

    /**
     * Activates the plan tab.
     */
    const handlePlanTabClick = () => {
        handleTabChange(TAB_PLAN);
    };

    /**
     * Activates the integrations/API tab.
     */
    const handleIntegrationsTabClick = () => {
        handleTabChange(TAB_INTEGRATIONS);
    };

    /**
     * Activates the AI tab.
     */
    const handleAiTabClick = () => {
        handleTabChange(TAB_AI);
    };

    /**
     * Activates the theme tab.
     */
    const handleThemeTabClick = () => {
        handleTabChange(TAB_THEME);
    };

    /**
     * Opens Plan settings from the members seat-limit upgrade CTA.
     */
    const handleUpgradePlanClick = () => {
        handleTabChange(TAB_PLAN);
    };

    /**
     * Opens the delete base confirmation modal.
     */
    const handleDeleteBaseClick = () => {
        clearMessages();
        setIsMobileMenuOpen(false);
        setDeleteBaseConfirmOpen(true);
    };

    /**
     * Toggles the mobile settings menu dropdown.
     */
    const handleMobileMenuToggle = () => {
        setIsMobileMenuOpen((prev) => !prev);
    };

    const navigationContents = (
        <>
            {(canSeeBaseSettings || canSeeSources) && (
                <>

                    {/* NAV GROUP LABEL */}
                    <div
                      className="settings-modal-nav-group-label"
                    >
                        BASE
                    </div>

                    {canSeeBaseSettings ? (
                        <SettingsModalButton
                          iconKey="edit"
                          label="About"
                          selected={activeTab === TAB_ABOUT}
                          onClick={handleAboutTabClick}
                        />
                    ) : null}

                    {canSeeBaseSettings ? (
                        <SettingsModalButton
                          iconKey="group"
                          label="Members"
                          selected={activeTab === TAB_MEMBERS}
                          onClick={handleMembersTabClick}
                        />
                    ) : null}

                    {canSeeBaseSettings ? (
                        <SettingsModalButton
                          iconKey="badge"
                          label="Roles"
                          selected={activeTab === TAB_ROLES}
                          onClick={handleRolesTabClick}
                        />
                    ) : null}

                    {canSeeBaseSettings ? (
                        <SettingsModalButton
                          iconKey="deployed_code"
                          label="Collections"
                          selected={activeTab === TAB_COLLECTIONS}
                          onClick={handleCollectionsTabClick}
                        />
                    ) : null}

                    {canSeeSources ? (
                        <SettingsModalButton
                          iconKey="hub"
                          label="Sources"
                          selected={activeTab === TAB_SOURCES}
                          onClick={handleSourcesTabClick}
                        />
                    ) : null}

                    {canSeeBaseSettings ? (
                        <SettingsModalButton
                          iconKey="description"
                          label="Documents"
                          selected={activeTab === TAB_DOCUMENTS}
                          onClick={handleDocumentsTabClick}
                        />
                    ) : null}

                    {canSeeBaseSettings ? (
                        <SettingsModalButton
                          iconKey="attach_money"
                          label="Financials"
                          selected={activeTab === TAB_FINANCIALS}
                          onClick={handleFinancialsTabClick}
                        />
                    ) : null}

                    {canSeeBaseSettings ? (
                        <SettingsModalButton
                          iconKey="receipt_long"
                          label="Purchase Orders"
                          selected={activeTab === TAB_PURCHASE_ORDERS}
                          onClick={handlePurchaseOrdersTabClick}
                        />
                    ) : null}

                    {canSeeBaseSettings ? (
                        <SettingsModalButton
                          iconKey="tag"
                          label="Sequences"
                          selected={activeTab === TAB_SEQUENCES}
                          onClick={handleSequencesTabClick}
                        />
                    ) : null}

                    {canSeeBaseSettings ? (
                        <SettingsModalButton
                          iconKey="credit_card"
                          label="Plan and Usage"
                          selected={activeTab === TAB_PLAN}
                          onClick={handlePlanTabClick}
                        />
                    ) : null}

                    {canSeeBaseSettings ? (
                        <SettingsModalButton
                          iconKey="bolt"
                          label="AI Model"
                          selected={activeTab === TAB_AI}
                          onClick={handleAiTabClick}
                        />
                    ) : null}

                    {canSeeBaseSettings ? (
                        <SettingsModalButton
                          iconKey="api"
                          label="API"
                          selected={activeTab === TAB_INTEGRATIONS}
                          onClick={handleIntegrationsTabClick}
                        />
                    ) : null}

                    {canSeeBaseSettings ? (
                        <SettingsModalButton
                          iconKey="palette"
                          label="Theme"
                          selected={activeTab === TAB_THEME}
                          onClick={handleThemeTabClick}
                        />
                    ) : null}

                    {canSeeBaseSettings && canDeleteBase ? (
                        <div className="settings-modal-nav-footer">
                            <SettingsModalButton
                              iconKey="delete"
                              label="Delete Workspace"
                              color={theme.warning}
                              onClick={handleDeleteBaseClick}
                            />
                        </div>
                    ) : null}
                </>
            )}
        </>
    );

    /**
     * Clears the active workspace locally and in user settings.
     * Main's first-workspace onboarding opens when the roster is empty.
     */
    const clearActiveWorkspace = async () => {
        setActiveBase && setActiveBase(null);

        if (!user?.key) return;

        const patch = buildActiveBaseSettingPatch('');
        setUserSettings && setUserSettings({
            ...(userSettings || {}),
            ...patch,
            userKey: user.key,
        });

        try {
            if (userSettings?.id || userSettings?.userKey) {
                await updateSetting(user.key, patch);
            } else {
                await createSetting(user.key, patch);
            }
        } catch (error) {
            if (error?.code === 'not-found') {
                await createSetting(user.key, patch);
                return;
            }
            console.error('[SettingsModal] Failed to clear ACTIVE_BASE_KEY:', error);
        }
    };

    /**
     * Queues deletion, then switches to another workspace or first-workspace create.
     */
    const handleConfirmDeleteBase = async () => {
        if (isDeletingBase) return;

        const baseKey = String(base?.key || '').trim();
        if (!baseKey) {
            setDeleteBaseConfirmOpen(false);
            return;
        }

        const alternatives = (Array.isArray(rosterBases) ? rosterBases : [])
            .filter((row) => getBaseKey(row) !== baseKey);

        setIsDeletingBase(true);

        try {
            await deleteBaseByKey(baseKey);

            hideBase && hideBase(baseKey);
            setDeleteBaseConfirmOpen(false);
            handleClose();

            if (alternatives.length > 0) {
                await handleBaseSelect(alternatives[0]);
                toast.success('Workspace deleted.');
            } else {
                await clearActiveWorkspace();
                toast.success('Workspace deleted. Create a new one to continue.');
            }
        } catch (error) {
            console.error('[SettingsModal] Failed to delete base:', error);
            unhideBase && unhideBase(baseKey);
            toast.error(error?.message || 'Unable to delete this workspace right now. Please try again.');
        } finally {
            setIsDeletingBase(false);
        }
    };

    // ----- RENDER ----- //

    // const handlePayoutsTabClick = () => {
    //     setErrorMessage('');
    //     setStatusMessage('');
    //     setActiveTab(TAB_PAYOUTS);
    // };

    return (
        <>
            <Modal
              iconKey="settings"
              title={`Settings / ${base?.title || 'Base'}`}
              isOpen={isOpen}
              height={isMobile ? '100%' : '800px'}
              width={isMobile ? '100%' : '920px'}
              onClose={handleClose}
            >
                <div className="settings-modal-container">

                    {/* NAV */}
                    {!isCompactChrome && (
                        <div
                          className="settings-modal-nav"
                        >
                            {navigationContents}
                        </div>
                    )}

                    {/* PANE */}
                    <div
                      className={[
                        'settings-modal-pane',
                        activeTab === TAB_THEME ? 'settings-modal-pane-fill' : '',
                      ].filter(Boolean).join(' ')}
                      style={{
                            margin: isCompactChrome ? '12px' : '0px',
                        }}
                    >
                        {isCompactChrome && (

                            <div className="settings-modal-mobile-menu">

                                {/* MOBILE MENU BUTTON */}
                                <button
                                  className="settings-modal-mobile-menu-button"
                                  style={{
                                    backgroundColor: isMobileMenuOpen ? theme.backgroundHover : theme.backgroundMuted,
                                  }}
                                  type="button"
                                  aria-expanded={isMobileMenuOpen}
                                  aria-label="Open settings menu"
                                  onClick={handleMobileMenuToggle}
                                >

                                    {/* MOBILE MENU BUTTON MAIN */}
                                    <div className="settings-modal-mobile-menu-button-main">

                                        {/* MOBILE MENU BUTTON ICON */}
                                        <div className="settings-modal-mobile-menu-button-icon">
                                            <Icon
                                              iconKey="menu"
                                              width={18}
                                              height={18}
                                              color={theme.foregroundMuted}
                                            />
                                        </div>

                                        {/* MOBILE MENU BUTTON TITLE */}
                                        <div
                                          className="settings-modal-mobile-menu-button-title"
                                        >
                                            Menu
                                        </div>
                                    </div>

                                    {/* MOBILE MENU BUTTON MAIN */}
                                    <div className="settings-modal-mobile-menu-button-main">

                                        {/* MOBILE MENU BUTTON VALUE */}
                                        <div
                                          className="settings-modal-mobile-menu-button-value"
                                        >
                                            {activeTabLabel}
                                        </div>

                                        {/* MOBILE MENU BUTTON ICON */}
                                        <div className="settings-modal-mobile-menu-button-icon">
                                            <Icon
                                              iconKey={isMobileMenuOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                                              width={18}
                                              height={18}
                                              color={theme.foregroundMuted}
                                            />
                                        </div>
                                    </div>
                                </button>

                                {isMobileMenuOpen && (
                                    <>

                                        {/* MOBILE MENU BACKDROP */}
                                        <div
                                          className="settings-modal-mobile-menu-backdrop"
                                          onClick={() => setIsMobileMenuOpen(false)}
                                        />

                                        {/* MOBILE MENU POPUP */}
                                        <div
                                          className="settings-modal-mobile-menu-popup"
                                        >

                                            {/* MOBILE MENU POPUP NAV */}
                                            <div className="settings-modal-mobile-menu-popup-nav">
                                                {navigationContents}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {canSeeBaseSettings && activeTab === TAB_ABOUT && (
                            <SettingsAboutPane
                              theme={theme}
                              base={base}
                              canEditBase={canEditBase}
                            />
                        )}

                        {canSeeBaseSettings && activeTab === TAB_MEMBERS && (
                            <SettingsMembersPane
                              theme={theme}
                              subtitle={membersPaneSubtitle}
                              canInviteMembers={canInviteMembers}
                              canUpgradePlan={canEditBase}
                              isAtSeatLimit={isAtSeatLimit}
                              seatLimitDescription={seatLimit.seatLimitDescription}
                              base={base}
                              errorMessage={errorMessage}
                              statusMessage={statusMessage}
                              sortedMembers={sortedMembers}
                              user={user}
                              canManageTarget={canManageTarget}
                              handleRoleChange={handleRoleChange}
                              handleRemoveMember={handleRemoveMember}
                              roleOptions={roleOptions}
                              getMemberRoleLabel={getMemberRoleLabel}
                              onInviteClick={onInviteClick}
                              onUpgradePlanClick={handleUpgradePlanClick}
                            />
                        )}

                        {canSeeBaseSettings && activeTab === TAB_PLAN && (
                            <SettingsPlanPane
                              user={user}
                              base={base}
                              canEditBase={canEditBase}
                              preferredPlanKey={preferredPlanKey}
                            />
                        )}

                        {canSeeBaseSettings && activeTab === TAB_ROLES && (
                            <SettingsRolesPane
                              theme={theme}
                              base={base}
                            />
                        )}

                        {canSeeBaseSettings && activeTab === TAB_COLLECTIONS && (
                            <SettingsCollectionsPane
                              theme={theme}
                              base={base}
                            />
                        )}

                        {canSeeBaseSettings && activeTab === TAB_DOCUMENTS && (
                            <SettingsDocumentsPane
                              theme={theme}
                              base={base}
                              canEditBase={canEditBase}
                            />
                        )}

                        {canSeeBaseSettings && activeTab === TAB_FINANCIALS && (
                            <SettingsQuickBooksPane
                              base={base}
                              canEditBase={canEditBase}
                            />
                        )}

                        {canSeeBaseSettings && activeTab === TAB_PURCHASE_ORDERS && (
                            <SettingsPurchaseOrdersPane
                              theme={theme}
                              base={base}
                              canEditBase={canEditBase}
                            />
                        )}

                        {canSeeBaseSettings && activeTab === TAB_SEQUENCES && (
                            <SettingsSequencesPane
                              theme={theme}
                              base={base}
                              canEditBase={canEditBase}
                            />
                        )}

                        {canSeeBaseSettings && activeTab === TAB_INTEGRATIONS && (
                            <SettingsIntegrationsPane />
                        )}

                        {canSeeBaseSettings && activeTab === TAB_AI && (
                            <SettingsAiPane
                              base={base}
                            />
                        )}

                        {canSeeBaseSettings && activeTab === TAB_THEME && (
                            <SettingsThemePane
                              base={base}
                            />
                        )}

                    </div>

                </div>
            </Modal>

            <Confirm
              title="Delete Workspace"
              message="Delete this workspace and everything in it? This action cannot be undone."
              isOpen={deleteBaseConfirmOpen}
              setOpen={setDeleteBaseConfirmOpen}
              busy={isDeletingBase}
              confirmLabel={isDeletingBase ? 'Deleting…' : 'Confirm'}
              onConfirm={handleConfirmDeleteBase}
            />
        </>
    );

});

// Dev Tools
SettingsModal.displayName = 'SettingsModal';

export default SettingsModal;
